/**
 * Global single-flight token refresh for the tenant-admin-dashboard.
 *
 * Every authed fetch wrapper/client refreshes through THIS module so that N
 * concurrent 401s trigger exactly ONE refresh round-trip. Awaiters share the
 * same in-flight promise; on failure the session is cleared and the error is
 * rethrown so callers can surface the "session expired" prompt.
 *
 * Previously each wrapper owned its own (or no) refresh promise, so parallel
 * calls raced each other and the server-side refresh lock.
 */

import {
  clearStoredSession,
  isStoredSessionPersistent,
  readStoredSession,
  writeStoredSession,
} from "@/lib/authSession";
import { refreshTenantDashboardToken } from "@/api/authApi";
import { RefreshInvalidError, RefreshTransientError } from "@/lib/sessionExpiredEvent";

let _refreshPromise = null;
let _lastTransientFailureAt = 0;

// After a transient refresh failure (429/5xx/network), don't hammer the refresh
// endpoint again for this long — a 3s poller would otherwise stampede it.
const TRANSIENT_COOLDOWN_MS = 5000;

/**
 * Refresh the stored dashboard session, deduplicated across all callers.
 * Resolves to the new access token.
 *
 * Failure handling:
 *  - RefreshInvalidError (refresh token rejected) → clear the session + rethrow.
 *    This is the ONLY path that ends the session.
 *  - RefreshTransientError (429/5xx/network) → leave the session intact, start a
 *    cooldown, and rethrow so the caller fails this one request quietly.
 * @returns {Promise<string>}
 */
export async function refreshDashboardSession() {
  if (typeof window === "undefined") {
    throw new Error("Session refresh is only available in the browser.");
  }
  if (!_refreshPromise) {
    // Storm control: a recent transient failure short-circuits new refreshes.
    if (_lastTransientFailureAt && Date.now() - _lastTransientFailureAt < TRANSIENT_COOLDOWN_MS) {
      throw new RefreshTransientError("Token refresh is cooling down after a recent failure.");
    }
    _refreshPromise = (async () => {
      const current = readStoredSession();
      const refreshed = await refreshTenantDashboardToken(current?.token);
      writeStoredSession({
        ...current,
        ...refreshed,
        email: refreshed.email || current?.email || "",
        rememberMe: isStoredSessionPersistent(),
      });
      _lastTransientFailureAt = 0;
      return refreshed.token;
    })()
      .catch((error) => {
        if (error instanceof RefreshInvalidError) {
          clearStoredSession();
        } else {
          // Transient (or unknown) — keep the session; back off briefly.
          _lastTransientFailureAt = Date.now();
        }
        throw error;
      })
      .finally(() => {
        _refreshPromise = null;
      });
  }
  return _refreshPromise;
}

/** Test-only: reset module state (singleton). */
export function __resetRefreshCoordinator() {
  _refreshPromise = null;
  _lastTransientFailureAt = 0;
}
